import React from "react";
import { Link } from "react-router-dom";
import { BarChart3, CalendarDays, Crosshair, Home, Import, Trophy } from "lucide-react";
import { Competition, CompetitionEvent } from "../data/types";
import { usePortalLink, useIsAdminArea } from "../utils/portalLink";

export default function CompetitionSidebar({
  competition,
  active,
  activeEventId,
  events = [],
}: {
  competition: Competition;
  active: string;
  activeEventId?: string;
  /** The competition's own events — supplied by the page that already loaded them. */
  events?: CompetitionEvent[];
}) {
  const link = usePortalLink();
  const isAdmin = useIsAdminArea();
  // Every target goes through link() so the sidebar keeps you in the portal you
  // are already in — an admin clicking "Matches" should not land on the public site.
  const base = link(`/competitions/${competition.id}`);
  const firstEvent = events[0];
  const items = [
    { key: "overview", label: "Overview", icon: Home, to: base },
    {
      /*
       * These are the meeting's shoot days. Labelling them "Events" made a
       * two-day meeting look like two events; the event is the meeting itself,
       * which is what the page above this sidebar is about.
       */
      key: "events",
      label: "Days",
      icon: CalendarDays,
      to: firstEvent ? `${base}/events/${firstEvent.id}` : base,
      count: competition.events,
    },
    {
      key: "competitions",
      label: "Competitions",
      icon: Trophy,
      to: `${base}/results`,
      count: competition.competitions ?? 1,
    },
    { key: "matches", label: "Matches", icon: Crosshair, to: `${base}/matches`, count: competition.matches },
    /*
     * Results is NOT in this list. It is the button above the menu: styling it
     * as a filled row here made two items look chosen at once — whichever page
     * you were on carried the active mark, and Results sat below it permanently
     * blue. A button and a menu answer different questions.
     */
    /*
     * This event's placings, not the portal's.
     *
     * The row used to open the portal-wide board — everybody who has ever shot,
     * on their lifetime average — which inside an event answers a question
     * nobody standing at that meeting is asking. Working out who had won took
     * reading the match cards one by one. The portal board is linked from the
     * bottom of the page it now opens.
     */
    { key: "rankings", label: "Rankings", icon: BarChart3, to: `${base}/rankings` },
    /*
     * Import History was the one entry in the old "Quick Links" card that was
     * not already a row in this nav. The rest of that card — Results and
     * Rankings — pointed at the same two places as the rows above it, so it
     * was a second copy of the menu rather than a shortcut past it.
     */
    ...(isAdmin
      ? [{ key: "imports", label: "Import History", icon: Import, to: "/admin/import-history", chevron: true }]
      : []),
  ];

  return (
    <aside className="w-full shrink-0 space-y-6 lg:w-[212px]">
      {/*
       * Navigation only.
       *
       * This card used to open with a tall gradient banner and then repeat the
       * event name, dates and location — the same three facts the page heading
       * prints two inches to its right. A fact shown twice on one screen does
       * not reinforce itself; it makes the reader stop and check whether the
       * two copies agree. The heading owns identity, this owns where you can go.
       */}
      {/* What almost everybody opens an event to reach, above the menu. */}
      <Link
        to={`${base}/results`}
        className="flex h-14 w-full items-center justify-center gap-2.5 rounded-xl bg-primary text-base font-extrabold text-white shadow-md transition-colors hover:bg-primary-hover"
      >
        <BarChart3 size={20} /> View Results
      </Link>

      <div className="card overflow-hidden">
        <nav className="space-y-0.5 p-3">
          {items.map(({ key, label, icon: Icon, to, count, chevron }: any) => (
            <Link
              key={key}
              to={to}
              className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors ${
                active === key
                  ? "border-l-[3px] border-primary bg-primary-soft text-primary"
                  : "text-slate-600 hover:bg-slate-50 hover:text-ink"
              }`}
            >
              <Icon size={16} />
              <span className="flex-1">{label}</span>
              {count !== undefined && (
                <span className="rounded-md bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-500">{count}</span>
              )}
              {chevron && <span className="text-slate-300">›</span>}
            </Link>
          ))}
          {active === "events" && !!events.length && (
            <div className="ml-8 space-y-0.5 border-l border-line pl-3 pt-1">
              {events.map((e) => (
                <Link
                  key={e.id}
                  to={`${base}/events/${e.id}`}
                  className={`block rounded-md px-2.5 py-1.5 text-[13px] font-medium transition-colors ${
                    activeEventId === e.id ? "bg-primary-soft text-primary" : "text-muted hover:text-ink"
                  }`}
                >
                  Day {e.n}: {e.name}
                </Link>
              ))}
            </div>
          )}
        </nav>
      </div>

    </aside>
  );
}
